import {
  Box,
  FormControl,
  ListItem,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { fontSize, palette } from '../../..';
import { flowOverrideTypes, TFlowOverrideType } from '../constants';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useDispatch } from 'react-redux';
import {
  updateDimensionOverride,
  updateFieldOfDimensionOverride,
} from '../../../redux/Calculations/CalculationsActions';
import { IFlowOverrideField } from '../../../redux/Calculations/CurrentCalculationSessionInterface';
import DimensionOverrideDropZone from '../DropZones/DimensionOverrideDropZone';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { datePatternCollections } from '../../FilterUtils/FilterPatternCollection';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { CanvasSelect } from '../common/CanvasSelect';

interface IDimensionOverrideProps {
  propKey: string;
  dimensionOverrideInfo: {
    type: TFlowOverrideType;
    dimensions: IFlowOverrideField[];
  };
  sx?: React.CSSProperties;
  ensureNodeVisible:()=>void
}
function DimensionOverride({ sx, propKey, dimensionOverrideInfo, ensureNodeVisible }: IDimensionOverrideProps) {
  const dispatch = useDispatch();
  const [hideDimensionOverride, setHideDimensionOverride] = useState(false);
  const handleTimeGrainChange = (value: string, fieldId: string) => {
    const field = dimensionOverrideInfo.dimensions.find((field) => field.uid === fieldId);
    if (!field) return;
    dispatch(updateFieldOfDimensionOverride(propKey, { ...field, timeGrain: value }, 'update'));
  };
  const handleChange = (event: SelectChangeEvent) => {
    const value = event.target.value as TFlowOverrideType;
    dispatch(
      updateDimensionOverride(propKey, {
        dimensions: value.includes('All') ? [] : dimensionOverrideInfo.dimensions,
        type: value,
      }),
    );
    if (value.includes("Some")) ensureNodeVisible()
  };
  return (
    <Box
      sx={{
        ...sx,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: "2px" }}>
        <Typography
          sx={{
            color: '#484848',
            fontSize: fontSize.large,
            fontWeight: 'bold',
            textAlign: 'left',
          }}
        >
          Dimension Override
        </Typography>
        {!hideDimensionOverride ? (
          <KeyboardArrowDownIcon
            onClick={() => setHideDimensionOverride(true)}
            sx={{ cursor: 'pointer', color: palette.primary.contrastText, mr: 0.7 }}
          />
        ) : (
          <KeyboardArrowRightIcon
            onClick={() => setHideDimensionOverride(false)}
            sx={{ cursor: 'pointer', color: palette.primary.contrastText, mr: 0.7 }}
          />
        )}
      </Box>
      {!hideDimensionOverride && (
        <>
          <Box sx={{ mb: dimensionOverrideInfo.type.includes('All') ? 2 : 0 }}>
            <CanvasSelect<(typeof flowOverrideTypes)[number]>
              value={flowOverrideTypes.find((t) => t.key === dimensionOverrideInfo.type) ?? null}
              options={flowOverrideTypes}
              getLabel={(opt) => opt.value}
              getKey={(opt) => opt.key}
              onChange={(val) =>
                handleChange({ target: { value: val.key } } as any)
              }
              style={{height : "2rem"}}
            />
          </Box>


          {!dimensionOverrideInfo.type.includes('All') && (
            <DimensionOverrideDropZone propKey={propKey}>
              {dimensionOverrideInfo.dimensions.length > 0 ? (
                <Box>
                  {dimensionOverrideInfo.dimensions.map((field) => (
                    <ListItem
                      key={field.uid}
                      sx={{
                        border: '1px solid grey',
                        borderRadius: '5px',
                        paddingInline: '0.3rem',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        height: '1.5rem',
                        width: '100%',
                        gap: '0.2rem',
                        boxSizing: 'border-box',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <CloseRoundedIcon
                        onClick={() => {
                          dispatch(updateFieldOfDimensionOverride(propKey, field, 'rmv'));
                          // dispatch(deleteField(propKey, field.uid));
                        }}
                        sx={{
                          fontSize: fontSize.medium,
                          cursor: 'pointer',
                          color: palette.primary.contrastText,
                          '&:hover': {
                            color: 'red',
                            transform: 'scale(1.1)',
                          },
                        }}
                      />
                      <span
                        title={field.displayName}
                        style={{
                          width: '100px',
                          flexGrow: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',

                          color: palette.primary.contrastText,
                          textDecoration: dimensionOverrideInfo.type.includes('ignore')
                            ? 'line-through'
                            : 'none',
                        }}
                      >
                        {field.displayName}
                      </span>
                      {['date', 'timestamp'].includes(field.dataType) && (
                        <FormControl
                          sx={{
                            maxWidth: 120,
                            flexShrink: 0,
                            marginLeft: 'auto',
                          }}
                        >
                          <Select
                            title={field.timeGrain}
                            value={field.timeGrain}
                            onChange={(e) => handleTimeGrainChange(e.target.value, field.uid)}
                            sx={{
                              height: '1.5rem',
                              '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                              },
                              textAlign: 'left',
                              '.MuiSelect-icon': {
                                fontSize: '19px',
                                marginLeft: '20px',
                                color: palette.primary.contrastText,
                                right: 1,
                              },
                              '.MuiSelect-outlined': {
                                color: dimensionOverrideInfo.type.includes('ignore')
                                  ? 'orange'
                                  : 'primary.contrastText',
                              },
                              fontSize: fontSize.medium,
                            }}
                            // onChange={handleAggregationChange}
                            IconComponent={KeyboardArrowDownIcon}
                          >
                            {datePatternCollections.map((timeGrain, _index: number) => {
                              return (
                                <MenuItem
                                  sx={{
                                    fontSize: fontSize.small,
                                    height: '1.5rem',
                                    color: 'primary.contrastText',
                                  }}
                                  key={_index}
                                  value={timeGrain.key}
                                >
                                  {timeGrain.value}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      )}
                    </ListItem>
                  ))}
                </Box>
              ) : (
               <Box sx={{display : "flex" , justifyContent : "center", alignItems : "center",py: "1rem"}}>
                    <Typography
                      sx={{
                        color: '#484848',
                        fontSize: fontSize.extraSmall,
                        textAlign: 'left',
                        paddingInline: '0.3rem',
                      }}
                    >
                      Drag and drop fields here to override dimensions
                    </Typography>
               </Box>
              )}
            </DimensionOverrideDropZone>
          )}
        </>
      )}
    </Box>
  );
}

export default DimensionOverride;
